import ActionListStatusGroup from '@/components/ActionListCompletedActions';
import AddActionButton from '@/components/AddActionButton';
import CreateBlockModal from '@/components/CreateBlockModal';
import DeleteBlockModal from '@/components/DeleteBlockModal';
import ActionListItem from '@/components/DragAndDrop/ActionListItem';
import DnDBlockItem from '@/components/DragAndDrop/DnDBlockItem';
import DuplicateBlockModal from '@/components/DuplicateBlockModal';
import EditBlockModal from '@/components/EditBlockModal';
import KeyResults from '@/components/ProjectDetail/KeyResults';
import { keys } from '@/gql/global/keys';
import useIsDarkMode from '@/hooks/useIsDarkMode';
import { useCompleteBlockStatus, useDeleteBlock, useIncompleteBlockStatus } from '@/services/myPlan/hooks';
import { useProjectDetail, useUpdateProject } from '@/services/project/hooks';
import { useActionModal } from '@/stores/useActionModal';
import { IconChevronDown } from '@/theme/icons';
import { Block } from '@/types/block';
import { Category } from '@/types/category';
import { ProjectType } from '@/types/project';
import { getActionsByBlockId, getCanceledActions, getCompletedActions, getIncompleteActions } from '@/utils/action';
import { augmentCompletedActionsWithRecurringSeries } from '@/utils/recurringCompletedShadow';
import { resolveCategoryImageByName } from '@/utils/category';
import { generateLinearGradient } from '@/utils/color';
import rem from '@/utils/rem';
import { invalidateQueries } from '@/utils/tanStackQuery';
import {
  Box,
  Button,
  Divider,
  Editable,
  EditablePreview,
  EditableTextarea,
  Flex,
  Icon,
  List,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { useDisclosure } from '@chakra-ui/react';
import { orderBy } from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TextareaAutosize from 'react-textarea-autosize';

interface ProjectDropdownProps {
  project: ProjectType;
  category: Category;
  isExpanded: boolean;
  onToggle: (projectId: string) => void;
}

const ProjectDropdown: React.FC<ProjectDropdownProps> = ({ project, category, isExpanded, onToggle }) => {
  const { t } = useTranslation('CategorySpotlight');
  const { setActionModalOpen } = useActionModal();
  const toast = useToast();
  const isDarkMode = useIsDarkMode();
  // State for editable fields
  const [result, setResult] = useState(project.result);
  const [purpose, setPurpose] = useState(project.purpose);

  // Update project mutation
  const { mutateAsync: updateProject } = useUpdateProject();

  const handleToggle = useCallback(() => {
    onToggle(project.id);
  }, [onToggle, project.id]);

  // Update state when project prop changes
  useEffect(() => {
    setResult(project.result);
    setPurpose(project.purpose);
  }, [project.result, project.purpose]);

  // Handlers for editable fields
  const onSubmitResult = useCallback(
    async (value: string) => {
      if (value !== project.result && value.length > 0) {
        try {
          await updateProject({
            id: project.id,
            name: project.name,
            result: value,
            purpose: project.purpose,
            categoryId: project.categoryId,
            imageUrl: project.imageUrl ?? null,
            isArchived: project.isArchived,
            order: project.order,
          });

          toast({
            title: t('projects.toasts.resultUpdated'),
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        } catch (error) {
          toast({
            title: t('projects.toasts.updateError'),
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          setResult(project.result);
        }
        return;
      }
      setResult(project.result);
    },
    [project, updateProject, toast, t],
  );

  const onSubmitPurpose = useCallback(
    async (value: string) => {
      if (value !== project.purpose && value.length > 0) {
        try {
          await updateProject({
            id: project.id,
            name: project.name,
            result: project.result,
            purpose: value,
            categoryId: project.categoryId,
            imageUrl: project.imageUrl ?? null,
            isArchived: project.isArchived,
            order: project.order,
          });

          toast({
            title: t('projects.toasts.purposeUpdated'),
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
        } catch (error) {
          toast({
            title: t('projects.toasts.updateError'),
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          setPurpose(project.purpose);
        }
        return;
      }
      setPurpose(project.purpose);
    },
    [project, updateProject, toast, t],
  );

  // Create resolved background image URL using the same logic as HeroImage
  const getResolvedImageUrl = useCallback(() => {
    if (!project.imageUrl) {
      return `linear-gradient(135deg, ${category.color}66 0%, ${category.color}33 100%)`;
    }

    const resolvedUrl = resolveCategoryImageByName(project.imageUrl);
    const imageUrl = project.imageUrl.includes('images.unsplash.com')
      ? `${resolvedUrl}&w=800&h=600&fit=crop&crop=faces%2Cedges&dpr=2&q=37`
      : resolvedUrl;

    return `url(${imageUrl})`;
  }, [project.imageUrl, category.color]);

  // Fetch complete project data when expanded
  const { data: projectDetail, isLoading } = useProjectDetail(project.id, {
    enabled: isExpanded, // Only fetch when expanded
    cacheTime: 0,
  });

  // State for Actions and RPM Blocks dropdown
  const [isActionsExpanded, setIsActionsExpanded] = useState(false);
  const [selectedBlock, setSelectedBlock] = useState<Block | null>(null);

  // State for Create Block Modal
  const {
    isOpen: isOpenCreateBlockModal,
    onOpen: openCreateBlockModal,
    onClose: onCloseCreateBlockModal,
  } = useDisclosure();

  // State for Edit Block Modal
  const { isOpen: isOpenEditBlockModal, onOpen: openEditBlockModal, onClose: onCloseEditBlockModal } = useDisclosure();

  // State for Duplicate Block Modal
  const {
    isOpen: isOpenDuplicateBlockModal,
    onOpen: openDuplicateBlockModal,
    onClose: onCloseDuplicateBlockModal,
  } = useDisclosure();

  // State for Delete Block Modal
  const {
    isOpen: isOpenDeleteBlockModal,
    onOpen: openDeleteBlockModal,
    onClose: onCloseDeleteBlockModal,
  } = useDisclosure();

  const handleActionsToggle = useCallback(() => {
    setIsActionsExpanded((prev) => !prev);
  }, []);

  const onClickAddAction = useCallback(() => {
    setActionModalOpen(true, {
      categoryId: category?.id,
      projectId: project?.id,
      readonlyCategory: true,
      readonlyProject: true,
      readonlyTooltipLabel: t('projects.readonlyTooltip'),
      location: 'project',
    });
  }, [category?.id, project?.id, setActionModalOpen]);

  const onClickAddBlock = useCallback(() => {
    openCreateBlockModal();
  }, [openCreateBlockModal]);

  // Block mutation hooks
  const { mutate: completeBlockStatus } = useCompleteBlockStatus({
    onSuccess: () => {
      if (project?.id) {
        invalidateQueries([keys.project.detail(project.id).queryKey]);
      }
    },
  });

  const { mutate: incompleteBlockStatus } = useIncompleteBlockStatus({
    onSuccess: () => {
      if (project?.id) {
        invalidateQueries([keys.project.detail(project.id).queryKey]);
      }
    },
  });

  const { mutate: deleteBlock } = useDeleteBlock();

  // Block handler functions
  const onCompleteBlock = useCallback(
    (blockId: string) => {
      completeBlockStatus({ id: blockId });
    },
    [completeBlockStatus],
  );

  const onUncompleteBlock = useCallback(
    (blockId: string) => {
      incompleteBlockStatus({ id: blockId });
    },
    [incompleteBlockStatus],
  );

  const handleDeleteBlock = useCallback(
    (block: Block | undefined) => {
      if (!block) return;
      setSelectedBlock(block);
      openDeleteBlockModal();
    },
    [openDeleteBlockModal],
  );

  const onDeleteBlock = useCallback(() => {
    if (!selectedBlock) return;

    deleteBlock({
      blockId: selectedBlock.id,
      projectId: project?.id,
    });

    onCloseDeleteBlockModal();
    setSelectedBlock(null);
  }, [selectedBlock, deleteBlock, project?.id, onCloseDeleteBlockModal]);

  const handleEditBlock = useCallback(() => {
    openEditBlockModal();
  }, [openEditBlockModal]);

  const handleDuplicateBlock = useCallback(() => {
    openDuplicateBlockModal();
  }, [openDuplicateBlockModal]);

  const onUpdateBlock = useCallback(
    (updatedBlock: Block) => {
      if (project?.id) {
        invalidateQueries([keys.project.detail(project.id).queryKey]);
      }
      onCloseEditBlockModal();
      setSelectedBlock(null);
    },
    [project?.id, onCloseEditBlockModal],
  );

  // Filter actions without blockId (similar to ProjectDetail logic)
  const filteredActions = useMemo(() => {
    if (!projectDetail?.projectByPk) {
      return [];
    }

    // Filter actions without a blockId, then sort them
    return orderBy(
      projectDetail.projectByPk.actions.filter((action) => action.blockId === null),
      'projectOrder',
      'asc',
    );
  }, [projectDetail?.projectByPk]);

  // Separate filtered actions by status
  const { incompleteActions, completedActions, canceledActions } = useMemo(() => {
    const nonCanceled = filteredActions.filter((a) => !a.isCanceled);
    const incomplete = getIncompleteActions(nonCanceled, 'projectOrder');
    return {
      incompleteActions: incomplete,
      completedActions: augmentCompletedActionsWithRecurringSeries(
        incomplete,
        getCompletedActions(nonCanceled, 'modifiedAt'),
      ),
      canceledActions: getCanceledActions(filteredActions, 'projectOrder'),
    };
  }, [filteredActions]);

  // Filter blocks (similar to ProjectDetail logic)
  const filteredBlocks = useMemo(() => {
    if (!projectDetail?.projectByPk) {
      return [];
    }

    return orderBy(
      projectDetail.projectByPk.blocks?.filter(
        (block) => getActionsByBlockId(block.id, projectDetail.projectByPk?.actions || [])?.length,
      ) || [],
      'projectOrder',
      'asc',
    );
  }, [projectDetail?.projectByPk]);

  return (
    <Box position="relative">
      {/* Extended Background Container - Only visible when expanded */}
      {isExpanded && (
        <Box
          position="absolute"
          zIndex={0}
          top={0}
          right={0}
          bottom={0}
          left={0}
          overflow="hidden"
          borderRadius={rem(8)}
        >
          {/* Extended Background Image - Fixed height */}
          <Box
            position="absolute"
            top={0}
            right={0}
            left={0}
            height={rem(400)} // Fixed height so it doesn't stretch when content expands
            backgroundImage={getResolvedImageUrl()}
            backgroundSize="cover"
            backgroundPosition="center"
            backgroundRepeat="no-repeat"
          />

          {/* Gradient Overlay on Image - Fades to background color */}
          <Box
            position="absolute"
            top={0}
            right={0}
            left={0}
            height={rem(400)}
            background={
              isDarkMode
                ? 'linear-gradient(180deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.4) 40%, rgba(20, 39, 55, 0.8) 80%, rgba(20, 39, 55, 1) 100%)'
                : 'linear-gradient(180deg, rgba(255, 255, 255, 0.6) 0%, rgba(255, 255, 255, 0.4) 40%, rgba(240, 240, 240, 0.8) 80%, rgba(240, 240, 240, 1) 100%)'
            }
          />

          {/* Solid Background for Content Area */}
          <Box
            position="absolute"
            top={rem(400)}
            right={0}
            bottom={0}
            left={0}
            backgroundColor="background-tertiary" // Solid background color for Key Results and cards
          />
        </Box>
      )}

      {/* Project Card */}
      <Box
        position="relative"
        zIndex={1}
        overflow="hidden"
        height={rem(80)}
        borderRadius={rem(8)}
        cursor="pointer"
        transition="all 0.2s ease"
        onClick={handleToggle}
      >
        {/* Background Image - Only when not expanded */}
        {!isExpanded && (
          <>
            <Box
              position="absolute"
              top={0}
              right={0}
              bottom={0}
              left={0}
              backgroundImage={getResolvedImageUrl()}
              backgroundSize="cover"
              backgroundPosition="center"
              backgroundRepeat="no-repeat"
            />

            {/* Overlay - Only when not expanded */}
            <Box
              position="absolute"
              top={0}
              right={0}
              bottom={0}
              left={0}
              background="linear-gradient(135deg, rgba(0, 0, 0, 0.6) 0%, rgba(0, 0, 0, 0.3) 100%)"
            />
          </>
        )}

        {/* Content */}
        <Flex
          position="relative"
          zIndex={1}
          alignItems="center"
          justifyContent="space-between"
          height="full"
          paddingX={rem(20)}
        >
          <Text color="white" fontSize={rem(18)} fontWeight="600" textShadow="0 2px 4px rgba(0, 0, 0, 0.3)">
            {project.name}
          </Text>

          <Icon
            as={IconChevronDown}
            boxSize={rem(20)}
            color="white"
            transform={isExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
            transition="transform 0.2s ease"
            filter="drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))"
          />
        </Flex>
      </Box>

      {/* Expanded Content */}
      {isExpanded && (
        <Box position="relative" zIndex={1} marginTop={rem(0)} padding={rem(20)} paddingTop={rem(0)}>
          <VStack alignItems="stretch" minWidth={0} spacing={rem(10)}>
            {/* Ultimate Result */}
            <Flex
              flexDirection="column"
              width="full"
              minWidth={0}
              padding={rem(20)}
              borderRadius={rem(4)}
              boxShadow="0 8px 32px rgba(0,0,0,0.1)"
              backdropFilter="blur(10px)"
              backgroundColor="rgba(255, 255, 255, 0.15)"
            >
              <Text
                display="inline-block"
                width="fit-content"
                marginBottom={rem(12)}
                padding={`${rem(2)} ${rem(6)}`}
                color="black"
                fontSize="xs"
                fontWeight="bold"
                letterSpacing="1px"
                textTransform="uppercase"
                borderRadius={rem(3)}
                backgroundColor={category.color}
              >
                {t('projects.ultimateResult')}
              </Text>
              <Editable
                overflow="hidden"
                width="full"
                minWidth={0}
                height="full"
                color="white"
                fontSize="md"
                fontWeight="500"
                lineHeight="1.5"
                defaultValue={result}
                onChange={setResult}
                onSubmit={onSubmitResult}
                placeholder={t('projects.placeholders.ultimateResult')}
                value={result}
              >
                <EditablePreview
                  width="full"
                  height="full"
                  color="text-primary"
                  fontStyle={result ? 'normal' : 'italic'}
                  opacity={result ? 1 : 0.7}
                  whiteSpace="pre-wrap"
                  wordBreak="break-word"
                  overflowWrap="break-word"
                />
                <EditableTextarea
                  as={TextareaAutosize}
                  color="text-primary"
                  _focusVisible={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: 'none',
                    borderRadius: 4,
                  }}
                  _placeholder={{
                    fontStyle: 'italic',
                  }}
                  whiteSpace="pre-wrap"
                  wordBreak="break-word"
                  overflowWrap="break-word"
                  resize="none"
                  maxLength={300}
                />
              </Editable>
            </Flex>

            {/* Ultimate Purpose */}
            <Flex
              flexDirection="column"
              width="full"
              minWidth={0}
              padding={rem(20)}
              borderRadius={rem(4)}
              boxShadow="0 8px 32px rgba(0,0,0,0.1)"
              backdropFilter="blur(10px)"
              backgroundColor="rgba(255, 255, 255, 0.15)"
            >
              <Text
                display="inline-block"
                width="fit-content"
                marginBottom={rem(12)}
                padding={`${rem(2)} ${rem(6)}`}
                color="black"
                fontSize="xs"
                fontWeight="bold"
                letterSpacing="1px"
                textTransform="uppercase"
                borderRadius={rem(3)}
                backgroundColor={category.color}
              >
                {t('projects.ultimatePurpose')}
              </Text>
              <Editable
                overflow="hidden"
                width="full"
                minWidth={0}
                height="full"
                color="text-primary"
                fontSize="md"
                fontWeight="500"
                lineHeight="1.5"
                defaultValue={purpose}
                onChange={setPurpose}
                onSubmit={onSubmitPurpose}
                placeholder={t('projects.placeholders.ultimatePurpose')}
                value={purpose}
              >
                <EditablePreview
                  width="full"
                  height="full"
                  color="text-primary"
                  fontStyle={purpose ? 'normal' : 'italic'}
                  opacity={purpose ? 1 : 0.7}
                  whiteSpace="pre-wrap"
                  wordBreak="break-word"
                  overflowWrap="break-word"
                />
                <EditableTextarea
                  as={TextareaAutosize}
                  color="text-primary"
                  _focusVisible={{
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    border: 'none',
                    borderRadius: 4,
                  }}
                  _placeholder={{
                    fontStyle: 'italic',
                  }}
                  whiteSpace="pre-wrap"
                  wordBreak="break-word"
                  overflowWrap="break-word"
                  resize="none"
                  maxLength={300}
                />
              </Editable>
            </Flex>

            {/* Key Results - Using reusable component */}
            {projectDetail?.projectByPk && !isLoading ? (
              <KeyResults
                color={category.color}
                gradientColor={category.color}
                keyResults={projectDetail.projectByPk.key_results || []}
                projectId={project.id}
                isArchived={false}
                onCreateAction={() => {}}
                onCreateBlock={() => {}}
                isTransparent
              />
            ) : (
              <Box padding={rem(16)}>
                <Text color="rgba(255, 255, 255, 0.7)" fontSize="sm">
                  {isLoading ? t('projects.loadingKeyResults') : t('projects.noKeyResults')}
                </Text>
              </Box>
            )}

            {/* Actions and RPM Blocks Dropdown */}
            <Box marginX={rem(-20)}>
              {/* Dropdown Header Button */}
              <Button
                width="full"
                height={rem(34)}
                padding={`${rem(12)} ${rem(20)}`}
                color="white"
                fontSize="sm"
                fontWeight="900"
                background="#1E3A52"
                borderRadius={0}
                _hover={{
                  background: '#1E3A52',
                }}
                _active={{
                  background: '#1E3A52',
                }}
                transition="all 0.2s ease"
                onClick={handleActionsToggle}
                rightIcon={
                  <Icon
                    as={IconChevronDown}
                    boxSize={rem(16)}
                    color="white"
                    transform={isActionsExpanded ? 'rotate(180deg)' : 'rotate(0deg)'}
                    transition="transform 0.2s ease"
                  />
                }
              >
                {isActionsExpanded ? t('projects.hideActionsBlocks') : t('projects.showActionsBlocks')}
              </Button>

              {/* Dropdown Content */}
              {isActionsExpanded && (
                <Box
                  padding={rem(20)}
                  borderTop="none"
                  backdropFilter="blur(10px)"
                  backgroundColor="background-tertiary"
                >
                  {/* Actions Section */}
                  <Box marginBottom={rem(16)}>
                    <Flex alignItems="center" justifyContent="space-between" marginBottom={rem(12)}>
                      <Text textStyle="large" color="text-primary">
                        {t('projects.actionsTitle')}
                      </Text>
                      <AddActionButton
                        onExternalClick={onClickAddAction}
                        width={rem(28)}
                        height={rem(28)}
                        variant="secondary"
                        isDisabled={false}
                      />
                    </Flex>

                    {/* Actions List */}
                    <VStack alignItems="stretch" spacing={rem(8)}>
                      {incompleteActions.length > 0 ? (
                        <>
                          <List width="full">
                            {incompleteActions.slice(0, 5).map((action, index) => (
                              <ActionListItem
                                key={action.id}
                                item={action}
                                index={index}
                                dndPayload={{ index, containerId: 'projectIncompleteActionsList' }}
                                showIndex={false}
                                withScheduleButton
                                backgroundColor="background-primary"
                                disableRecurringStatusChanges
                              />
                            ))}
                          </List>

                          {/* Show more actions indicator */}
                          {incompleteActions.length > 5 && (
                            <Box marginTop={rem(8)}>
                              <Text color="rgba(255, 255, 255, 0.6)" fontSize="xs" textAlign="center">
                                +{incompleteActions.length - 5} {t('projects.moreActions')}
                              </Text>
                            </Box>
                          )}
                        </>
                      ) : (
                        <Text color="text-primary" fontSize="sm" fontStyle="italic">
                          {t('projects.noIncompleteActions')}
                        </Text>
                      )}

                      {/* Completed Actions Dropdown */}
                      <Box width="full">
                        <ActionListStatusGroup
                          actions={completedActions}
                          title={t('actions.completedActions')}
                          emptyMessage={t('actions.noCompletedActions')}
                          onClickCreateBlock={onClickAddAction}
                          onDeleteAction={() => {}}
                        />
                      </Box>
                    </VStack>
                  </Box>

                  {/* Separator */}
                  <Box marginX={rem(-20)} marginY={rem(20)}>
                    <Divider opacity={0.6} borderColor="rgba(255, 255, 255, 0.2)" />
                  </Box>

                  {/* RPM Blocks Section */}
                  <Box>
                    <Flex alignItems="center" justifyContent="space-between" marginBottom={rem(12)}>
                      <Text textStyle="large" color="text-primary">
                        {t('projects.rpmBlocksTitle')}
                      </Text>
                      <AddActionButton
                        tooltipText={t('projects.addNewBlock')}
                        onClick={onClickAddBlock}
                        variant="secondary"
                        width={rem(28)}
                        height={rem(28)}
                        isDisabled={false}
                      />
                    </Flex>

                    {filteredBlocks.length > 0 ? (
                      <VStack alignItems="stretch" spacing={rem(8)}>
                        {filteredBlocks.slice(0, 3).map((block, index) => (
                          <DnDBlockItem
                            key={block.id}
                            actions={getActionsByBlockId(block.id, projectDetail?.projectByPk?.actions || [])}
                            block={block}
                            index={index}
                            weeklyPlanId=""
                            onCompleteBlock={onCompleteBlock}
                            openSetUncompletedActionsModal={() => {}}
                            onUncompleteBlock={onUncompleteBlock}
                            onSelectBlock={(options) => {
                              setSelectedBlock({
                                ...block,
                                actions: getActionsByBlockId(block.id, projectDetail?.projectByPk?.actions || []),
                              });
                            }}
                            onClickAddAction={() => onClickAddAction()}
                            onClickRemoveFromBlock={() => {}}
                            withScheduleButton
                            openEditBlockModal={() => {
                              setSelectedBlock({
                                ...block,
                                actions: getActionsByBlockId(block.id, projectDetail?.projectByPk?.actions || []),
                              });
                              handleEditBlock();
                            }}
                            onDeleteBlock={handleDeleteBlock}
                            openDuplicateBlockModal={() => {
                              setSelectedBlock({
                                ...block,
                                actions: getActionsByBlockId(block.id, projectDetail?.projectByPk?.actions || []),
                              });
                              handleDuplicateBlock();
                            }}
                            collapsedByDefault={false}
                            isArchived={false}
                            sortable={false}
                            disableRecurringStatusChanges
                          />
                        ))}

                        {/* Show more blocks indicator */}
                        {filteredBlocks.length > 3 && (
                          <Box marginTop={rem(8)}>
                            <Text color="rgba(255, 255, 255, 0.6)" fontSize="xs" textAlign="center">
                              +{filteredBlocks.length - 3} {t('projects.moreBlocks')}
                            </Text>
                          </Box>
                        )}
                      </VStack>
                    ) : (
                      <Box
                        width="full"
                        padding={rem(20)}
                        textAlign="left"
                        background={generateLinearGradient(
                          category.color,
                          'var(--chakra-colors-background-primary)',
                          270,
                          0.15,
                        )}
                        border="1px solid"
                        borderColor={category.color}
                        borderRadius={rem(8)}
                        backdropFilter="blur(10px)"
                      >
                        <Text color="text-primary" fontSize="md" fontWeight="700">
                          {t('projects.noBlocksBanner')}
                        </Text>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          </VStack>
        </Box>
      )}

      {/* Create Block Modal */}
      {isOpenCreateBlockModal && (
        <CreateBlockModal
          isOpen={isOpenCreateBlockModal}
          onClose={onCloseCreateBlockModal}
          categoryId={category?.id}
          projectId={project?.id}
          readyOnlyCategory
          readOnlyProject
        />
      )}

      {/* Edit Block Modal */}
      {isOpenEditBlockModal && selectedBlock && (
        <EditBlockModal
          isOpen={isOpenEditBlockModal}
          block={{ ...selectedBlock, project: project }}
          onUpdateBlock={onUpdateBlock}
          onDeleteBlock={handleDeleteBlock}
          onCancel={onCloseEditBlockModal}
        />
      )}

      {/* Duplicate Block Modal */}
      {isOpenDuplicateBlockModal && selectedBlock && (
        <DuplicateBlockModal
          reScheduleBlock={false}
          block={selectedBlock}
          isOpen={isOpenDuplicateBlockModal}
          onClose={onCloseDuplicateBlockModal}
        />
      )}

      {/* Delete Block Modal */}
      {isOpenDeleteBlockModal && (
        <DeleteBlockModal isOpen={isOpenDeleteBlockModal} onClose={onCloseDeleteBlockModal} onSubmit={onDeleteBlock} />
      )}
    </Box>
  );
};

export default ProjectDropdown;
